import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Paperclip, X, ExternalLink, Loader2 } from "lucide-react";
import { useCustomFieldFiles } from "@/hooks/shared/useCustomFieldFiles";
import { formatFileSize, getFileIcon } from "@/lib/fileUtils";
import type { AttachmentValue } from "@/types/fieldRegistry";

interface AttachmentFieldInputProps {
  value: AttachmentValue[] | null | undefined;
  onChange: (next: AttachmentValue[]) => void;
  disabled?: boolean;
}

/**
 * Input para campos personalizados do tipo 'attachment'.
 * Lista os arquivos anexados (abrir via URL assinada / remover) e permite
 * enviar 1+ novos arquivos. O valor é um array de AttachmentValue salvo no
 * custom_data da entidade pelo caller (via onChange).
 */
export function AttachmentFieldInput({ value, onChange, disabled }: AttachmentFieldInputProps) {
  const files: AttachmentValue[] = Array.isArray(value) ? value : [];
  const { isUploading, uploadFiles, getFileUrl, removeFromStorage } = useCustomFieldFiles();
  const inputRef = useRef<HTMLInputElement>(null);
  const [openingPath, setOpeningPath] = useState<string | null>(null);

  const handleSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    e.target.value = ""; // permite reanexar o mesmo arquivo
    if (selected.length === 0) return;
    const uploaded = await uploadFiles(selected);
    if (uploaded.length > 0) onChange([...files, ...uploaded]);
  };

  const handleOpen = async (path: string) => {
    setOpeningPath(path);
    const url = await getFileUrl(path);
    setOpeningPath(null);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleRemove = async (idx: number) => {
    const target = files[idx];
    onChange(files.filter((_, i) => i !== idx));
    if (target?.path) await removeFromStorage(target.path);
  };

  return (
    <div className="space-y-2">
      {files.length > 0 && (
        <ul className="space-y-1">
          {files.map((f, i) => (
            <li
              key={`${f.path}-${i}`}
              className="flex items-center gap-2 rounded-md border bg-background px-2 py-1.5 text-sm"
            >
              <span className="shrink-0" aria-hidden>{getFileIcon(f.type)}</span>
              <button
                type="button"
                onClick={() => handleOpen(f.path)}
                className="flex-1 min-w-0 text-left truncate hover:underline"
                title={f.name}
              >
                {f.name}
              </button>
              <span className="shrink-0 text-xs text-muted-foreground">{formatFileSize(f.size)}</span>
              {openingPath === f.path ? (
                <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />
              ) : (
                <button
                  type="button"
                  onClick={() => handleOpen(f.path)}
                  className="shrink-0 text-muted-foreground hover:text-foreground"
                  title="Abrir"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </button>
              )}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemove(i)}
                  className="shrink-0 text-muted-foreground hover:text-destructive"
                  title="Remover"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {!disabled && (
        <>
          <input ref={inputRef} type="file" multiple hidden onChange={handleSelect} />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isUploading}
            onClick={() => inputRef.current?.click()}
          >
            {isUploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Enviando...
              </>
            ) : (
              <>
                <Paperclip className="h-4 w-4 mr-2" /> Anexar arquivo
              </>
            )}
          </Button>
        </>
      )}

      {disabled && files.length === 0 && (
        <span className="text-muted-foreground/50 italic text-xs">—</span>
      )}
    </div>
  );
}
